import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiChevronDown, FiExternalLink } = FiIcons;

const STATUS_COLORS = {
  new: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  contacted: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  qualified: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  'proposal sent': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  converted: 'text-green-400 bg-green-500/10 border-green-500/20',
  'not suitable': 'text-red-400 bg-red-500/10 border-red-500/20',
  closed: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20'
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [filterType, filterStatus]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await api.getLeads({
        type: filterType,
        status: filterStatus,
        search
      });
      setLeads(res.leads || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.updateLeadStatus(id, newStatus);
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
      if (selectedLead && selectedLead.id === id) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleExport = () => {
    window.location.href = api.getExportUrl();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap gap-4">
          <input 
            type="text" 
            placeholder="Search company, name, email..." 
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-xs text-white w-72 focus:border-blue-600 outline-none"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button type="submit" className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2.5 rounded-xl text-xs font-semibold">
            Search
          </button>
          <select 
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="contact">Contact Enquiries</option>
            <option value="intelligence_review">Intelligence Reviews</option>
          </select>
          <select 
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </form>

        <button 
          onClick={handleExport}
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all"
        >
          <SafeIcon icon={FiDownload} className="w-3.5 h-3.5 text-blue-500" />
          Export CSV
        </button>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-zinc-900 bg-zinc-900/40">
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Date</th>
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Type</th>
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Contact / Company</th>
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Service / Market</th>
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Status</th>
                <th className="py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {loading ? (
                <tr><td colSpan="6" className="py-20 text-center text-zinc-600 text-xs font-medium">Fetching database records...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan="6" className="py-20 text-center text-zinc-600 text-xs">No records found. Enquiries submitted on the website will appear here in real-time.</td></tr>
              ) : (
                leads.map(lead => (
                  <tr key={lead.id} className="hover:bg-zinc-900/30 transition-colors">
                    <td className="py-4 px-6 text-xs text-zinc-400 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap">
                      <span className={`text-[9px] font-bold uppercase px-2.5 py-1 rounded-md border ${
                        lead.lead_type === 'contact' 
                          ? 'bg-zinc-900 border-zinc-800 text-zinc-300' 
                          : 'bg-blue-900/20 border-blue-900/40 text-blue-400'
                      }`}>
                        {lead.lead_type === 'contact' ? 'Contact' : 'Review'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-xs font-bold text-white">{lead.full_name}</p>
                      <p className="text-[11px] text-zinc-400">{lead.company_name} {lead.job_title ? `• ${lead.job_title}` : ''}</p>
                      <a href={`mailto:${lead.work_email}`} className="text-[10px] text-blue-500 hover:underline">{lead.work_email}</a>
                    </td>
                    <td className="py-4 px-6 text-xs text-zinc-400">
                      <p className="text-xs text-zinc-300 font-medium">{lead.service_interest || 'Intelligence Review'}</p>
                      <p className="text-[10px] text-zinc-500">{lead.industry} ({lead.country})</p>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap">
                      <select 
                        value={lead.status} 
                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                        className={`text-[9px] font-bold uppercase px-3 py-1.5 rounded-lg border outline-none cursor-pointer ${STATUS_COLORS[lead.status] || 'bg-zinc-900 text-zinc-400 border-zinc-800'}`}
                      >
                        {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s} className="bg-zinc-900 text-white">{s}</option>)}
                      </select>
                    </td>
                    <td className="py-4 px-6 text-right whitespace-nowrap">
                      <button 
                        onClick={() => setSelectedLead(lead)}
                        className="text-xs text-blue-400 hover:text-blue-300 font-semibold px-3 py-1 bg-zinc-900 hover:bg-zinc-800 rounded-lg border border-zinc-800"
                      >
                        View Full
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for viewing detailed lead info */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl">
            <div className="flex justify-between items-start mb-6 border-b border-zinc-900 pb-4">
              <div>
                <span className="text-[9px] font-bold uppercase px-2.5 py-1 rounded bg-blue-900/20 text-blue-400 border border-blue-900/40">
                  {selectedLead.lead_type === 'contact' ? 'Contact Enquiry' : 'Intelligence Review Request'}
                </span>
                <h2 className="text-2xl font-serif font-bold text-white mt-2">{selectedLead.company_name}</h2>
                <p className="text-xs text-zinc-400">{selectedLead.full_name} ({selectedLead.job_title})</p>
              </div>
              <button 
                onClick={() => setSelectedLead(null)}
                className="text-zinc-500 hover:text-white text-lg font-bold p-2"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6 text-xs text-zinc-300">
              <div className="grid grid-cols-2 gap-4 bg-zinc-900/50 p-4 rounded-2xl border border-zinc-800/50">
                <div>
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block">Email</span>
                  <a href={`mailto:${selectedLead.work_email}`} className="text-blue-400 hover:underline">{selectedLead.work_email}</a>
                </div>
                <div>
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block">Telephone / WhatsApp</span>
                  <p className="text-white">{selectedLead.telephone}</p>
                </div>
                <div>
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block">Website</span>
                  <p className="text-white">{selectedLead.website || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block">Location</span>
                  <p className="text-white">{selectedLead.country} • {selectedLead.industry}</p>
                </div>
              </div>

              {selectedLead.contact_message && (
                <div className="bg-zinc-900/30 p-4 rounded-2xl border border-zinc-800/50">
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Message</span>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedLead.contact_message}</p>
                </div>
              )}

              {selectedLead.business_challenge && (
                <div className="bg-zinc-900/30 p-4 rounded-2xl border border-zinc-800/50">
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Primary Business / Marketing Challenge</span>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedLead.business_challenge}</p>
                </div>
              )}

              {selectedLead.competitors && (
                <div className="bg-zinc-900/30 p-4 rounded-2xl border border-zinc-800/50">
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Competitors to Monitor</span>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedLead.competitors}</p>
                </div>
              )}

              {selectedLead.competitive_concern && (
                <div className="bg-zinc-900/30 p-4 rounded-2xl border border-zinc-800/50">
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Main Competitive Concern</span>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedLead.competitive_concern}</p>
                </div>
              )}

              {selectedLead.desired_result && (
                <div className="bg-zinc-900/30 p-4 rounded-2xl border border-zinc-800/50">
                  <span className="text-zinc-500 font-bold uppercase text-[9px] block mb-1">Desired Result</span>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedLead.desired_result}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {selectedLead.monthly_advertising_range && (
                  <div>
                    <span className="text-zinc-500 font-bold uppercase text-[9px] block">Monthly Ad Spend Range</span>
                    <p className="text-white font-medium">{selectedLead.monthly_advertising_range}</p>
                  </div>
                )}
                {selectedLead.preferred_contact_method && (
                  <div>
                    <span className="text-zinc-500 font-bold uppercase text-[9px] block">Preferred Contact Method</span>
                    <p className="text-white font-medium">{selectedLead.preferred_contact_method}</p>
                  </div>
                )}
              </div>

              {(selectedLead.utm_source || selectedLead.utm_campaign) && (
                <div className="bg-zinc-900/20 p-3 rounded-xl text-[10px] text-zinc-500">
                  <span className="font-bold uppercase text-[9px] block mb-1">Attribution (UTM)</span>
                  Source: {selectedLead.utm_source || 'direct'} | Medium: {selectedLead.utm_medium || '-'} | Campaign: {selectedLead.utm_campaign || '-'}
                </div>
              )}
            </div>

            <div className="mt-8 pt-4 border-t border-zinc-900 flex justify-end">
              <button 
                onClick={() => setSelectedLead(null)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-wider"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}